import {
  isInlineLayoutActionDefinition,
  isNonRegistryLayoutActionDefinition,
} from '@/common/utils/elementLayoutActions'

const tryParseElementId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseInt(value.trim(), 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export interface PageLayoutElementIndex {
  ids: Set<number>
  names: Set<string>
}

const addName = (index: PageLayoutElementIndex, name: unknown) => {
  const normalized = String(name ?? '').trim()
  if (normalized) index.names.add(normalized)
}

const addId = (index: PageLayoutElementIndex, id: unknown) => {
  const parsed = tryParseElementId(id)
  if (parsed != null) index.ids.add(parsed)
}

type LayoutElementConfig = {
  _attributes?: { id?: unknown; name?: unknown }
  id?: unknown
  name?: { _text?: unknown }
}

const indexElementConfig = (config: LayoutElementConfig | undefined, index: PageLayoutElementIndex) => {
  if (!config) return
  addId(index, config._attributes?.id ?? config.id)
  addName(index, config.name?._text)
  addName(index, config._attributes?.name)
}

const indexPanelRows = (panelData: unknown, index: PageLayoutElementIndex) => {
  if (!panelData || typeof panelData !== 'object') return
  const rows = (panelData as { rows?: unknown }).rows
  if (!Array.isArray(rows)) return

  for (const rowItem of rows) {
    if (!Array.isArray(rowItem)) continue
    for (const elementItem of rowItem) {
      indexElementConfig(
        (elementItem as { element?: LayoutElementConfig })?.element,
        index,
      )
    }
  }
}

const indexPanelElements = (panelsMap: unknown, index: PageLayoutElementIndex) => {
  if (!panelsMap || typeof panelsMap !== 'object' || Array.isArray(panelsMap)) return

  for (const panelData of Object.values(panelsMap as Record<string, unknown>)) {
    indexPanelRows(panelData, index)
  }
}

/** Page/header/footer cells with inline elements (synthetic simple panels after getTabs). */
const indexLayoutRegions = (regions: unknown, index: PageLayoutElementIndex) => {
  if (regions == null) return
  const list = Array.isArray(regions) ? regions : [regions]
  for (const region of list) {
    if (!region) continue
    const tabs = Array.isArray(region) ? region : [region]
    for (const tab of tabs) {
      if (!tab || typeof tab !== 'object') continue
      const panelRows = (tab as { panelRows?: unknown }).panelRows
      if (!Array.isArray(panelRows)) continue
      for (const panelRow of panelRows) {
        const panels = Array.isArray(panelRow) ? panelRow : [panelRow]
        for (const panel of panels) {
          indexPanelRows(panel, index)
        }
      }
    }
  }
}

const collectFormActions = (formRoot: unknown): unknown[] => {
  if (!formRoot || typeof formRoot !== 'object') return []
  const root = formRoot as Record<string, unknown>
  const layout =
    root.panels != null || root.actions != null
      ? root
      : (root.form as Record<string, unknown> | undefined) ?? root
  const actions = layout.actions as { action?: unknown } | undefined
  const raw = actions?.action
  if (!raw) return []
  return Array.isArray(raw) ? raw : [raw]
}

export type LayoutActionStateLike = {
  element_level?: unknown
  element_type?: unknown
  element_id?: unknown
  element_name?: unknown
}

export function isNonRegistryLayoutActionState(
  state: LayoutActionStateLike | null | undefined,
  formRoot: unknown,
): boolean {
  if (!state || typeof state !== 'object') return false
  const level = String(state.element_level ?? state.element_type ?? '').toUpperCase()
  if (level !== 'ACTION') return false

  const stateId = tryParseElementId(state.element_id)
  const stateName = String(state.element_name ?? '').trim()

  for (const action of collectFormActions(formRoot)) {
    if (!action || typeof action !== 'object') continue
    if (!isNonRegistryLayoutActionDefinition(action)) continue
    const attrs = (action as { _attributes?: Record<string, unknown> })._attributes
    const actionId = tryParseElementId(attrs?.id)
    const actionName = String(
      (action as { name?: { _text?: unknown } }).name?._text ?? '',
    ).trim()
    if (
      (stateId != null && actionId != null && stateId === actionId) ||
      (stateName !== '' && actionName !== '' && stateName === actionName)
    ) {
      return true
    }
  }
  return false
}

const indexActions = (formData: Record<string, unknown>, index: PageLayoutElementIndex) => {
  for (const action of collectFormActions(formData)) {
    if (!action || typeof action !== 'object') continue
    if (isNonRegistryLayoutActionDefinition(action)) continue
    const attrs = (action as { _attributes?: Record<string, unknown> })._attributes
    addId(index, attrs?.id)
    addName(index, (action as { name?: { _text?: unknown } }).name?._text)
  }
}

const indexOverlays = (formData: Record<string, unknown>, index: PageLayoutElementIndex) => {
  const layoutRoot =
    formData.overlays != null
      ? formData
      : (formData.form as Record<string, unknown> | undefined)?.overlays != null
        ? (formData.form as Record<string, unknown>)
        : null
  const overlays = layoutRoot?.overlays
  if (!Array.isArray(overlays)) return
  for (const overlay of overlays) {
    if (!overlay || typeof overlay !== 'object') continue
    addId(index, (overlay as { id?: unknown }).id)
    addName(index, (overlay as { name?: unknown }).name)
  }
}

/** Индекс element_id / element_name из XML-разметки формы  */
export function buildPageLayoutElementIndex(formRoot: unknown): PageLayoutElementIndex {
  const index: PageLayoutElementIndex = { ids: new Set(), names: new Set() }
  if (!formRoot || typeof formRoot !== 'object') return index

  const root = formRoot as Record<string, unknown>
  const layout =
    root.panels != null || root.actions != null
      ? root
      : (root.form as Record<string, unknown> | undefined) ?? root

  indexPanelElements(layout.panels, index)
  indexLayoutRegions(layout.main, index)
  indexLayoutRegions(layout.header, index)
  indexLayoutRegions(layout.footer, index)
  indexActions(layout, index)
  indexOverlays(layout, index)
  return index
}

export function stateMatchesPageLayout(
  state: Record<string, unknown> | null | undefined,
  index: PageLayoutElementIndex,
): boolean {
  if (!state || typeof state !== 'object') return false

  const level = String(state.element_level ?? state.element_type ?? '').toUpperCase()
  if (level === 'FORM' || level === 'PAGE') {
    return true
  }

  const elementId = tryParseElementId(state.element_id)
  if (elementId != null && index.ids.has(elementId)) {
    return true
  }

  const elementName = String(state.element_name ?? '').trim()
  if (elementName && index.names.has(elementName)) {
    return true
  }

  return false
}

/** Отсечь states, не относящиеся к разметке текущего слота */
export function filterStatesForPageLayout(states: unknown[], formRoot: unknown): unknown[] {
  if (!Array.isArray(states) || states.length === 0) {
    return []
  }

  const index = buildPageLayoutElementIndex(formRoot)
  if (index.ids.size === 0 && index.names.size === 0) {
    return states
  }

  return states.filter((state) => {
    const record = state as Record<string, unknown>
    if (isNonRegistryLayoutActionState(record, formRoot)) {
      return false
    }
    return stateMatchesPageLayout(record, index)
  })
}
